import { useEffect, useState } from "react";
import api, { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Send, MessageCircle, Loader2, AlertTriangle, CheckCircle, Zap, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const personalize = (msg, name) => {
  const first = (name || "").trim().split(" ")[0] || "";
  return (msg || "").replace(/{prenom}/g, first).replace(/{name}/g, name || "").replace(/{nom}/g, name || "");
};

export default function CampaignLauncher({ slug, campaign, reload }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [twilio, setTwilio] = useState(false);
  const [pidx, setPidx] = useState(0);

  const load = async () => {
    setData(null); setPidx(0);
    try { const { data } = await api.get(`/store/${slug}/campaigns/${campaign.id}/recipients`); setData(data); }
    catch (e) { toast.error(apiError(e)); }
    api.get(`/store/${slug}/integrations`).then((r) => setTwilio(r.data.twilio_whatsapp)).catch(() => {});
  };

  const openDialog = () => { setOpen(true); load(); };
  const isEmail = campaign.channel === "Email";

  const markSent = async () => {
    setSending(true);
    try { const { data: r } = await api.post(`/store/${slug}/campaigns/${campaign.id}/send`); toast.success(`Campagne marquée envoyée · ${r.count} destinataires`); setOpen(false); reload && reload(); }
    catch (e) { toast.error(apiError(e)); }
    setSending(false);
  };

  const sendAuto = async () => {
    setSending(true);
    try { const { data: r } = await api.post(`/store/${slug}/campaigns/${campaign.id}/send-auto`); toast.success(`${r.sent} messages WhatsApp envoyés via Twilio`); setOpen(false); reload && reload(); }
    catch (e) { toast.error(apiError(e)); }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" onClick={openDialog} className="text-emerald-400 hover:text-emerald-300 h-8 mr-1" data-testid="campaign-launch-btn">
          <Send size={14} className="mr-1" /> Lancer
        </Button>
      </DialogTrigger>
      <DialogContent className="vp-card border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{campaign.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Canal {campaign.channel} · segment « {campaign.segment || "Tous"} » · Clics suivis: <b className="text-white mono">{campaign.stats?.click ?? 0}</b></DialogDescription>
        </DialogHeader>

        {isEmail ? (
          <div className="py-4">
            <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={18} />
              <p className="text-sm text-slate-300">L'envoi d'<b>emails marketing en masse</b> n'est pas disponible sur cette plateforme (emails transactionnels uniquement). Passez la campagne en canal <b>WhatsApp</b> pour la diffuser à vos clients.</p>
            </div>
          </div>
        ) : (
          <div className="py-2">
            {!data ? <div className="py-6 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement des destinataires…</div> : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-400"><b className="text-white mono">{data.count}</b> destinataire(s)</p>
                  {twilio ? <span className="text-xs text-emerald-400 flex items-center gap-1"><Zap size={12} /> Twilio actif</span>
                    : <span className="text-xs text-slate-500">Twilio non configuré</span>}
                </div>
                {data.count > 0 && (() => { const list = data.recipients; const idx = Math.min(pidx, list.length - 1); const r = list[idx]; return (
                  <div className="bg-black/20 rounded-lg p-3 mb-3" data-testid="campaign-preview">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-xs text-slate-500">Aperçu{r?.name ? ` — ${r.name}` : ""}</div>
                      {list.length > 1 && (
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-white" onClick={() => setPidx((idx - 1 + list.length) % list.length)} data-testid="preview-prev"><ChevronLeft size={14} /></Button>
                          <span className="text-xs text-slate-400 mono" data-testid="preview-index">{idx + 1}/{list.length}</span>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-white" onClick={() => setPidx((idx + 1) % list.length)} data-testid="preview-next"><ChevronRight size={14} /></Button>
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-slate-200 whitespace-pre-wrap" data-testid="preview-body">{personalize(campaign.body || campaign.subject || "", r?.name)}</div>
                  </div>
                ); })()}
                {campaign.status === "Envoyée" && <div className="flex items-center gap-2 text-sm text-emerald-400 mb-3"><CheckCircle size={16} /> Déjà envoyée ({campaign.stats?.recipients ?? 0} destinataires)</div>}
                <div className="space-y-2 max-h-64 overflow-y-auto no-scrollbar">
                  {data.recipients.map((r, i) => {
                    const phone = (r.whatsapp || r.phone || "").replace(/\D/g, "");
                    const link = `${API}/public/track/wa/${campaign.id}?to=${encodeURIComponent(phone)}&name=${encodeURIComponent(r.name || "")}`;
                    return (
                      <div key={i} className="flex items-center justify-between bg-black/20 rounded-lg p-2.5" data-testid="campaign-recipient">
                        <div className="min-w-0"><div className="text-sm text-white truncate">{r.name}</div><div className="text-xs text-slate-500">{r.phone}</div></div>
                        {phone ? (
                          <a href={link} target="_blank" rel="noreferrer" data-testid="campaign-wa-link">
                            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white h-8"><MessageCircle size={14} className="mr-1" /> Envoyer</Button>
                          </a>
                        ) : <span className="text-xs text-slate-600">Pas de numéro</span>}
                      </div>
                    );
                  })}
                  {data.recipients.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Aucun client dans ce segment</p>}
                </div>
                <p className="text-xs text-slate-500 mt-2">Astuce: utilisez <span className="mono">{"{prenom}"}</span> dans le message pour insérer automatiquement le prénom du client. Chaque envoi est comptabilisé dans les clics. Les ouvertures ne sont pas mesurables sur WhatsApp.</p>
              </>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Fermer</Button>
          {!isEmail && data && data.count > 0 && (
            <>
              {twilio && (
                <Button onClick={sendAuto} disabled={sending} className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium" data-testid="campaign-send-auto">
                  {sending && <Loader2 className="animate-spin mr-2" size={15} />} <Zap size={14} className="mr-1" /> Envoi auto (Twilio)
                </Button>
              )}
              <Button onClick={markSent} disabled={sending} className="vp-accent-bg text-black font-medium" data-testid="campaign-mark-sent">
                {sending && <Loader2 className="animate-spin mr-2" size={15} />} Marquer comme envoyée
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
